import theme from "../../../../theme";
import { $, $$ } from "@utils/dom";
import { on, off } from "@utils/listener";
import SwiperCore, { Swiper, Pagination, EffectCreative, EffectFade } from 'swiper';
import Breakpoint from "@utils/breakpoint";
import Viewport from "@utils/viewport";
import { mobile } from "@utils/mobile";

const MOBILE_VIEWPORT = parseInt(theme['grid-breakpoints']['md']);
const BREAKPOINTS = [`(min-width: ${MOBILE_VIEWPORT}px)`];

SwiperCore.use([Pagination]);

const SWIPER_OPTIONS_POSTS_MOBILE = {
  allowTouchMove: true,
  loop: true,
  slidesPerView: 1,
  autoHeight: true,
  pagination: {
    el: '.pb-row-featured-posts__pagination',
    clickable: true
  },
};

const SWIPER_OPTIONS_POSTS = {
  modules: [EffectFade],
  effect: "fade",
  allowTouchMove: false,
  loop: false,
  fadeEffect: {
    crossFade: true
  },
};

const SLIDER_OPTIONS_THUMBNAILS = {
  allowTouchMove: false,
  slidesPerView: 1,
  spaceBetween: 0,
  speed: 850,
  loop: true,
  ... !mobile ? {
    modules: [EffectCreative],
    effect: "creative",
    creativeEffect: {
      prev: {
        translate: ["-75%", 0, -1],
        scale: ["1"]
      },
      next: {
        translate: ["100%", 0, 0],
        scale: ["1.125"]
      }
    }
  } : null
};

const SELECTOR_SLIDER_THUMBNAILS = `.pb-row-featured-posts__sliderPostThumbnails`;
const SELECTOR_SLIDER_POSTS = `.pb-row-featured-posts__sliderPosts`;
const SELECTOR_SLIDER_BTN_NEXT = `.pb-row-featured-posts__sliderBtn.--next`;
const SELECTOR_SLIDER_BTN_NEXT_IMAGES = `.pb-row-featured-posts__sliderBtn__postImage`;
const CLASSNAME_THUMBNAIL_IMAGE_SELECTED = `--selected`;

class PbRowFeaturedPosts {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;

    // elements
    this.sliderThumbnails = $(SELECTOR_SLIDER_THUMBNAILS, this.el);
    this.sliderPosts = $(SELECTOR_SLIDER_POSTS, this.el);
    this.nextSlideBtn = $(SELECTOR_SLIDER_BTN_NEXT, this.el);
    this.nextSlideBtnImages = $$(SELECTOR_SLIDER_BTN_NEXT_IMAGES, this.nextSlideBtn);

    // bindings
    this._onBreakpointChange = this._onBreakpointChange.bind(this);
    this._goToNextSlide = this._goToNextSlide.bind(this);
    this._onSlideChangeStart = this._onSlideChangeStart.bind(this);
    this._onSlideChangeEnd = this._onSlideChangeEnd.bind(this);
    this._bp = new Breakpoint(BREAKPOINTS, this._onBreakpointChange);

    // state
    this._isMobile = false;
    this._inTransition = false;
    this._currentSlideIndex = 0;

    this.init();
  }

  init() {
    if (this._bp) this._bp.run();
    else this._onBreakpointChange();

    this._setActivePostThumbnail();
    this._bindEvents();
  }

  destroy() {
    this._unbindEvents();
    this.el = null;
    this.emitter = null;
    this.sliderThumbnails = null;
    this.sliderPosts = null;
    this.nextSlideEl = null;
    this._onBreakpointChange = null;
    this._goToNextSlide = null;
    this._onSlideChangeStart = null;
    this._onSlideChangeEnd = null;
    this._isMobile = null;
    this._inTransition = false;
    if(this._bp) this._bp.dispose();
    if(this.swiperThumbnails) this.swiperThumbnails.destroy();
    if(this.swiperPosts) this.swiperPosts.destroy();
  }

  _bindEvents() {
    if (this.nextSlideBtn) on(this.nextSlideBtn, "click", this._goToNextSlide);
    this._bp?.on();
  }

  _unbindEvents() {
    if (this.nextSlideBtn) off(this.nextSlideBtn, "click", this._goToNextSlide);
    this._bp?.off();
  }

  _onBreakpointChange() {
    this._isMobile = Viewport.width < MOBILE_VIEWPORT;

    if(this.swiperThumbnails) this.swiperThumbnails.destroy();
    if(this.swiperPosts) this.swiperPosts.destroy();

    // thumbnails slider
    if (this.sliderThumbnails) {
      this.swiperThumbnails = new Swiper(this.sliderThumbnails, {
        ...SLIDER_OPTIONS_THUMBNAILS,
        on: {
          slideChangeTransitionEnd: this._onSlideChangeEnd
        }
      });
    }

    // posts slider
    if (this.sliderPosts) {
      this.swiperPosts = new Swiper(this.sliderPosts, {
        ...(this._isMobile ? SWIPER_OPTIONS_POSTS_MOBILE : SWIPER_OPTIONS_POSTS),
        on: {
          slideChangeTransitionStart: this._onSlideChangeStart,
          slideChangeTransitionEnd: () => {
            if(this._isMobile) this._onSlideChangeEnd()
          }
        }
      });
    }

  }

  _goToNextSlide() {
    // on mobile, always go to next slide
    if(this._isMobile) this.swiperPosts.slideNext();

    // on other devices, do nothing when in transition
    if (this._inTransition) return;

    if (this.swiperPosts.isEnd) {
      this.swiperPosts.slideTo(0);
    } else {
      this.swiperPosts.slideNext();
    }
  }

  _onSlideChangeStart(swiper) {
    const { realIndex } = swiper;
    this._inTransition = true;
    this._currentSlideIndex = realIndex;
    this._setActivePostThumbnail();
    if (this.swiperThumbnails && !this._isMobile) this.swiperThumbnails.slideNext();
  }

  _onSlideChangeEnd() {
    this._inTransition = false;
  }

  _setActivePostThumbnail() {
    // find next image in index
    const maxIndex = this.nextSlideBtnImages.length - 1;
    const nextImageIndex = this._currentSlideIndex + 1 > maxIndex ? 0 : this._currentSlideIndex + 1;

    // reset all images
    this.nextSlideBtnImages.forEach((i) => i.classList.remove(CLASSNAME_THUMBNAIL_IMAGE_SELECTED));

    // set next image selected
    const img = this.nextSlideBtnImages[nextImageIndex];
    img.classList.add(CLASSNAME_THUMBNAIL_IMAGE_SELECTED);
  }
}

export default PbRowFeaturedPosts;
